import { useContext, useState } from 'react';

import { fetchPutSellerPricing } from '@iwoca/lapi-client/edge';
import { AlertBox, Button } from '@iwoca/orion';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import styles from './ReusablePaylinkPage.module.css';
import { ActivationContext } from '../../../activation/Activation';
import { PricingType } from '../../../api/lending/edge';
import { TPricingTypes } from '../../../Buyer/PayLinkLanding/utils/PayLinkLanding.types';
import { LoadingSpinner } from '../../../components/LoadingSpinner/LoadingSpinner';
import { LockedButton } from '../../../components/LockedButton/LockedButton';
import { Modal } from '../../../components/Modal/Modal';
import { getIwocaPayMeUrl } from '../../../components/PayMeLink/getIwocaPayMeUrl';
import { PayMeLink } from '../../../components/PayMeLink/PayMeLink';
import { useSellerHandle } from '../../../hooks/useSellerHandle.hook';
import { useStateKey } from '../../../hooks/useStateKey.hook';
import { CheckoutPreview } from '../components/CheckoutPreview/CheckoutPreview';
import { PayLinkPricingFormField } from '../components/PayLinkPricingFormField/PayLinkPricingFormField';
import {
  Column,
  VerticalSplitLayout,
} from '../components/VerticalSplitLayout/VerticalSplitLayout';
import { usePayIn12 } from '../hooks/usePayIn12';
import payLinksPageStyles from '../PayLinks.module.css';
import { DEFAULT_12M_PRICING_OPTION } from '../PayLinksNavigation';
import { usePreview } from '../usePreview';

export const ReusablePaylinkPage = () => {
  const { previewValues, isModularPricing, setPreviewValuesFromEvent } =
    usePreview();

  return (
    <VerticalSplitLayout className={payLinksPageStyles.PayLinksPage}>
      <Column>
        <div className={payLinksPageStyles.leftSide}>
          <h1 className={payLinksPageStyles.title}>
            A reusable pay link for any customer
          </h1>
          <p className={payLinksPageStyles.description}>
            Designed to be added to your invoice template and website. Your
            customer enters the amount and reference, and chooses to Pay Now or
            Pay Later.
          </p>
          <p className={payLinksPageStyles.description}>
            If you’ve linked your Xero, this’ll be the link your customers get
            on their invoices.
          </p>
          {previewValues?.pricing_3m ? (
            <UpdateSellerPricingForm
              isModularPricing={isModularPricing}
              onChange={setPreviewValuesFromEvent}
              pricingTypes={{
                pricing_3m: previewValues.pricing_3m?.type as PricingType,
                pricing_12m: previewValues.pricing_12m?.type as PricingType,
              }}
            />
          ) : null}
        </div>
      </Column>
      <Column>
        {!!previewValues.pricing_3m ? (
          <CheckoutPreview
            sellerName={previewValues.sellerName}
            pricingPromotions={{
              THREE_MONTHS: previewValues.pricing_3m.promotions,
              TWELVE_MONTHS: previewValues.pricing_12m?.promotions,
            }}
            representativeInterest={{
              THREE_MONTHS: previewValues.pricing_3m.representativeInterest,
              TWELVE_MONTHS: previewValues.pricing_12m?.representativeInterest,
            }}
          />
        ) : (
          <div>Loading preview...</div>
        )}
      </Column>
    </VerticalSplitLayout>
  );
};

const ERROR_MESSAGE =
  'Error updating the pricing. Please refresh the page and try again.';

const UpdateSellerPricingForm = ({
  onChange,
  pricingTypes,
  isModularPricing,
}: {
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  pricingTypes: TPricingTypes;
  isModularPricing: boolean;
}) => {
  const { stateKey } = useStateKey();
  const { sellerHandle } = useSellerHandle();
  const [hasSubmitError, setHasSubmitError] = useState(false);
  const [isConfirmPricingModalVisible, setIsConfirmPricingModalVisible] =
    useState(false);
  const { payIn12Enabled } = usePayIn12();

  if (!sellerHandle) return null;
  if (!stateKey) return null;

  const payMeUrl = getIwocaPayMeUrl(sellerHandle);

  return (
    <Formik
      initialValues={{
        pricing_3m: pricingTypes.pricing_3m,
        pricing_12m: pricingTypes.pricing_12m || DEFAULT_12M_PRICING_OPTION,
      }}
      validationSchema={Yup.object().shape({
        pricing_3m: Yup.string().required('Please provide a pay link pricing'),
      })}
      onSubmit={async (values, actions) => {
        actions.setSubmitting(true);
        setHasSubmitError(false);
        try {
          await fetchPutSellerPricing({
            stateKey,
            body: {
              data: {
                active: values.pricing_3m,
                active_product_pricing: {
                  THREE_MONTHS: { type: values.pricing_3m },
                  ...(payIn12Enabled && {
                    TWELVE_MONTHS: { type: values.pricing_12m },
                  }),
                },
              },
            },
          });
        } catch (e) {
          setHasSubmitError(true);
        }
        setIsConfirmPricingModalVisible(false);
        actions.setSubmitting(false);
      }}
    >
      {({
        values,
        handleChange,
        dirty,
        resetForm,
        submitForm,
        isSubmitting,
      }: FormikProps<{
        pricing_3m: PricingType;
        pricing_12m: PricingType;
      }>) => {
        return (
          <>
            <Form>
              {hasSubmitError ? (
                <AlertBox
                  variant="failure"
                  className={payLinksPageStyles.submitError}
                >
                  {ERROR_MESSAGE}
                </AlertBox>
              ) : null}
              {isModularPricing ? (
                <PayLinkPricingFormField
                  productPricing={values}
                  onChange={(e) => {
                    onChange(e);
                    handleChange(e);
                  }}
                />
              ) : null}
              {isModularPricing && dirty ? (
                <PricingChange
                  onConfirm={() => setIsConfirmPricingModalVisible(true)}
                  onCancel={resetForm}
                />
              ) : (
                <div className={payLinksPageStyles.payLinkContainer}>
                  <PayMeLink payMeUrl={payMeUrl} />
                </div>
              )}
            </Form>
            <ConfirmationModal
              isOpen={isConfirmPricingModalVisible}
              isSubmitting={isSubmitting}
              onConfirm={async () => {
                await submitForm();
                resetForm({
                  values: {
                    pricing_3m: values.pricing_3m,
                    pricing_12m: values.pricing_12m,
                  },
                });
              }}
              onCancel={() => {
                resetForm();
                setIsConfirmPricingModalVisible(false);
              }}
            >
              <>
                <h2>Change the terms for your Universal link</h2>
                <p>
                  It’ll update for everyone who uses this pay link - unless
                  they’ve already started check out.
                </p>
              </>
            </ConfirmationModal>
          </>
        );
      }}
    </Formik>
  );
};

const PricingChange = ({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  const { isOnboarded, isLoading } = useContext(ActivationContext);

  if (isLoading) return null;

  return (
    <>
      {isOnboarded ? (
        <div className={payLinksPageStyles.buttonWrapper}>
          <Button variant="secondary" onClick={onCancel}>
            Don't save
          </Button>
          <Button
            variant="primary"
            type="submit"
            //@ts-expect-error
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
          >
            Confirm
          </Button>
        </div>
      ) : (
        <div>
          <LockedButton />
        </div>
      )}
    </>
  );
};

const ConfirmationModal = ({
  isOpen,
  isSubmitting,
  onConfirm,
  onCancel,
  children,
}: {
  isOpen: boolean;
  isSubmitting: boolean;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
  children: React.ReactNode;
}) => {
  return (
    <Modal
      isModalOpen={isOpen}
      isClosable={false}
      testPrefix="confirmPricingChangeModal"
    >
      <div className={styles.confirmPricingChangeModal}>
        {children}
        <Button onClick={onConfirm} variant="primary" disabled={isSubmitting}>
          {!isSubmitting ? (
            <span>Yes, update my pay link</span>
          ) : (
            <LoadingSpinner />
          )}
        </Button>
        {!isSubmitting ? (
          <Button variant="tertiary" onClick={onCancel}>
            Discard change
          </Button>
        ) : null}
      </div>
    </Modal>
  );
};
